import { Printer } from 'lucide-react'

interface Props {
  /** Project display name shown as the eyebrow above the document title. */
  projectName?: string
  /** Total stories — kept for backwards compatibility; rendered only as accessible label. */
  totalCount: number
}

/**
 * Thin meta strip above the document body. The page identity ("Requirements ·
 * N items") already lives in the topbar — restating it here is redundancy that
 * pushes the first section heading below the fold.
 *
 * Roles kept:
 *   - Project eyebrow (only when projectName is provided) — context for printed output.
 *   - Print affordance (hidden when printing) — secondary action, low visual weight.
 */
export function DocumentHeader({ projectName, totalCount }: Props) {
  return (
    <div
      role="group"
      className="flex items-center justify-between mb-4"
      aria-label={`Product requirements — ${totalCount.toLocaleString()} ${totalCount === 1 ? 'item' : 'items'}`}
    >
      <div className="min-w-0">
        {projectName && (
          <div
            className="font-mono truncate"
            style={{
              fontSize: 11,
              color: 'var(--color-fg-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            {projectName} · Product requirements
          </div>
        )}
      </div>
      <div data-print-hide className="flex items-center gap-2 print:hidden flex-shrink-0">
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md cursor-pointer border-0 transition-colors"
          style={{
            background: 'transparent',
            color: 'var(--color-fg-muted)',
            fontSize: 12,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--color-warm-100)'
            e.currentTarget.style.color = 'var(--color-fg-secondary)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent'
            e.currentTarget.style.color = 'var(--color-fg-muted)'
          }}
          title="Print or save as PDF"
          aria-label="Print or save as PDF"
        >
          <Printer size={12} />
          Print
        </button>
      </div>
    </div>
  )
}
